/**
 * 마크다운 표를 HTML 표로 변환합니다.
 */
const parseMarkdownTable = (text: string): string => {
  // 표 패턴을 더 간단하게 찾기
  const lines = text.split('\n');
  const result: string[] = [];
  let i = 0;
  
  while (i < lines.length) {
    const line = lines[i].trim();
    
    // 표 시작을 찾음 (| 로 시작하고 끝나는 라인)
    if (line.startsWith('|') && line.endsWith('|') && line.includes('|')) {
      const headerLine = line;
      const separatorLine = lines[i + 1]?.trim();
      
      // 다음 줄이 구분선인지 확인 (|---|---|---| 형태)
      if (separatorLine && separatorLine.startsWith('|') && separatorLine.includes('-')) {
        const tableRows = [headerLine];
        let j = i + 2; // 구분선 다음부터 시작
        
        // 연속된 표 행들을 찾음
        while (j < lines.length && lines[j].trim().startsWith('|') && lines[j].trim().endsWith('|')) {
          tableRows.push(lines[j].trim());
          j++;
        }
        
        if (tableRows.length > 1) { // 최소 헤더 + 1개 데이터 행
          const tableHtml = convertTableToHtml(tableRows);
          result.push(tableHtml);
          i = j; // 다음 처리할 라인으로 이동
          continue;
        }
      }
    }
    
    result.push(lines[i]);
    i++;
  }
  
  return result.join('\n');
};

/**
 * 표 행들을 HTML로 변환
 */
const convertTableToHtml = (tableRows: string[]): string => {
  if (tableRows.length === 0) return '';
  
  const headerRow = tableRows[0];
  const dataRows = tableRows.slice(1);
  
  // 헤더 파싱
  const headers = headerRow
    .trim()
    .split('|')
    .slice(1, -1) // 첫 번째와 마지막 빈 요소 제거
    .map((header: string) => header.trim());
  
  // 데이터 행들 파싱
  const rows = dataRows.map((row: string) => 
    row.trim()
       .split('|')
       .slice(1, -1) // 첫 번째와 마지막 빈 요소 제거
       .map((cell: string) => cell.trim())
  );
  
  // HTML 표 생성
  let tableHtml = '<table class="markdown-table">';
  
  // 헤더 생성
  if (headers.length > 0) {
    tableHtml += '<thead><tr>';
    headers.forEach((header: string) => {
      tableHtml += `<th>${header}</th>`;
    });
    tableHtml += '</tr></thead>';
  }
  
  // 본문 생성
  if (rows.length > 0) {
    tableHtml += '<tbody>';
    rows.forEach((row: string[]) => {
      if (row.length > 0) {
        tableHtml += '<tr>';
        row.forEach((cell: string) => {
          tableHtml += `<td>${cell}</td>`;
        });
        tableHtml += '</tr>';
      }
    });
    tableHtml += '</tbody>';
  }
  
  tableHtml += '</table>';
  return tableHtml;
};

/**
 * 텍스트 내의 URL, 이메일, 전화번호, 마크다운 표를 감지하여 변환합니다.
 */
export const linkify = (text: string): string => {
  // console.log('===================== LINKIFY 함수 실행 시작 =====================');
  // console.log('입력 텍스트:', text);

  if (!text) {
    // console.log('텍스트가 비어있음, 빈 문자열 반환');
    return '';
  }

  // 먼저 마크다운 표를 HTML 표로 변환
  text = parseMarkdownTable(text);

  // 이미 변환된 a 태그는 변환하지 않기 위한 처리
  // 모든 a 태그를 임시 토큰으로 치환
  const aTags: string[] = [];
  let processedText = text.replace(/<a[^>]*>.*?<\/a>/g, (match) => {
    aTags.push(match);
    return `__ATAG_${aTags.length - 1}__`;
  });

  // console.log('A 태그 추출 후:', processedText);

  // Pattern to identify markdown-style links - 순서를 첫번째로 변경하고 캡처 그룹 개선
  // [텍스트](링크주소) 형태의 마크다운 링크
  const markdownLinkPattern = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;

  // URL 패턴 수정 - 괄호 안의 URL을 올바르게 추출하기 위한 패턴
  // 괄호로 시작하거나 끝나는 URL을 제외하기 위한 패턴 개선
  const urlPattern = /(?<!\()(?:https?:\/\/|www\.)[^\s()<>"']+(?!\))/g;

  // Pattern to identify phone numbers (국내 전화번호 패턴)
  // 044-111-1111, 044)111-1111, 044) 111-1111 등 지원
  const phonePattern = /(\d{2,4}\)?[-\s]?\d{3,4}[-\s]?\d{4}|\d{2,3}[-\s]?\d{4}[-\s]?\d{4}|044[-\s]?120)/g;

  // Pattern to identify emails
  const emailPattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;

  // First replace markdown-style links with HTML <a> tags
  const markdownMatches: string[] = [];
  processedText = processedText.replace(markdownLinkPattern, (match, p1, p2) => {
    // 임시 토큰 포함 검사
    if (match.includes('__ATAG_')) {
      return match;
    }
    const result = `<a href="${p2}" target="_blank" rel="noopener noreferrer">${p1}</a>`;
    markdownMatches.push(`${match} -> ${result}`);
    return result;
  });

  if (markdownMatches.length > 0) {
    // console.log('마크다운 링크 변환:', markdownMatches);
  }

  // 괄호 안의 URL을 처리하는 특별한 패턴
  const parenthesizedUrlPattern = /\(([a-zA-Z]+:\/\/[^\s)]+|www\.[^\s)]+)\)/g;
  processedText = processedText.replace(parenthesizedUrlPattern, (match, url) => {
    if (match.includes('__ATAG_')) {
      return match;
    }

    // 이미 마크다운 링크로 처리된 URL은 건너뜀
    if (match.startsWith('](')) {
      return match;
    }

    const fullUrl = url.startsWith('www.') ? `http://${url}` : url;
    return `(<a href="${fullUrl}" target="_blank" rel="noopener noreferrer">${url}</a>)`;
  });

  // Then replace plain URLs with clickable links
  const urlMatches: string[] = [];
  processedText = processedText.replace(urlPattern, (url, offset, str) => {
    // 이미 <a> 태그 내부에 있는 URL은 건너뜀 (임시 태그 내부는 처리하지 않음)
    if (url.includes('__ATAG_')) {
      return url;
    }

    // 앞뒤 문맥 검사 추가 - a 태그 안에 있는 URL은 처리하지 않음
    const surroundingText = str.substring(Math.max(0, offset - 20), offset + url.length + 20);
    if (surroundingText.includes('__ATAG_') || surroundingText.includes('<a href="')) {
      return url;
    }

    // 마크다운 링크 안에 있는 URL은 처리하지 않음
    const beforeText = str.substring(Math.max(0, offset - 5), offset);
    if (beforeText.includes('](')) {
      return url;
    }

    // URL 끝부분에 붙은 특수문자(괄호, 쉼표 등) 제거
    let cleanUrl = url;
    if (url.endsWith(')') || url.endsWith(',') || url.endsWith('.') || url.endsWith(';')) {
      cleanUrl = url.slice(0, -1);
      const lastChar = url.slice(-1);

      // Add http protocol to www links if missing
      const fullUrl = cleanUrl.startsWith('www.') ? `http://${cleanUrl}` : cleanUrl;
      const result = `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer">${cleanUrl}</a>${lastChar}`;
      urlMatches.push(`${url} -> ${result}`);
      return result;
    }

    // Add http protocol to www links if missing
    const fullUrl = url.startsWith('www.') ? `http://${url}` : url;
    const result = `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    urlMatches.push(`${url} -> ${result}`);
    return result;
  });

  if (urlMatches.length > 0) {
    // console.log('URL 링크 변환:', urlMatches);
  }

  // 전화번호 링크 변환 함수
  const convertPhoneNumber = (phoneNumber: string, surroundingContext: string): string => {
    // URL의 일부인 경우 변환하지 않음
    if (surroundingContext.includes('http://') ||
      surroundingContext.includes('https://') ||
      surroundingContext.includes('www.')) {
      return phoneNumber;
    }

    // 이미 링크 내부에 있으면 변환하지 않음
    if (surroundingContext.includes('<a href="') || surroundingContext.includes('__ATAG_')) {
      return phoneNumber;
    }

    // 번호 정제 - 숫자만 추출 (044)111-1111 → 0441111111, tel: 스킴 호환)
    const cleanNumber = phoneNumber.replace(/\D/g, '');
    return `<a href="tel:${cleanNumber}" class="phone-link">${phoneNumber}</a>`;
  };

  // Replace phone numbers with tel: links for mobile call functionality
  const phoneMatches: string[] = [];
  processedText = processedText.replace(phonePattern, (phoneNumber, _, offset, str) => {
    // URL의 키 값이거나 쿼리 파라미터인 경우 변환하지 않음
    const beforeText = str.substring(Math.max(0, offset - 30), offset);
    const afterText = str.substring(offset + phoneNumber.length, Math.min(str.length, offset + phoneNumber.length + 30));

    if ((beforeText.includes('http') || beforeText.includes('www')) &&
      (afterText.includes('/') || afterText.includes('?') || afterText.includes('&'))) {
      return phoneNumber;
    }

    // HTML 태그 안에 있는 전화번호도 처리하지 않음
    if (/<[^>]*$/.test(str.substring(0, offset)) && /^[^<]*>/.test(str.substring(offset + phoneNumber.length))) {
      return phoneNumber;
    }

    const surroundingText = str.substring(Math.max(0, offset - 20), offset + phoneNumber.length + 20);
    const result = convertPhoneNumber(phoneNumber, surroundingText);

    if (result !== phoneNumber) {
      phoneMatches.push(`${phoneNumber} -> ${result}`);
    }

    return result;
  });

  if (phoneMatches.length > 0) {
    // console.log('전화번호 링크 변환:', phoneMatches);
  }

  // Linkify emails
  const emailMatches: string[] = [];
  processedText = processedText.replace(emailPattern, (email, offset, str) => {
    // 임시 토큰이 포함된 문자열 안에 있는 이메일은 처리하지 않음
    const surroundingText = str.substring(Math.max(0, offset - 20), offset + email.length + 20);
    if (surroundingText.includes('__ATAG_') || surroundingText.includes('<a href="')) {
      return email;
    }

    const result = `<a href="mailto:${email}">${email}</a>`;
    emailMatches.push(`${email} -> ${result}`);
    return result;
  });

  if (emailMatches.length > 0) {
    // console.log('이메일 링크 변환:', emailMatches);
  }

  // Replace exact "기능개선 요청" with hyperlink
  processedText = processedText.replace(/(?<!["'>])기능 개선 요청(?!["'<])/g, `<a href="https://www.sejong.go.kr/prog/bbs/646/eeungDB/sub03_01/write.do" target="_blank" rel="noopener noreferrer">기능개선 요청</a>`);


  // 임시 토큰을 원래 a 태그로 복원
  processedText = processedText.replace(/__ATAG_(\d+)__/g, (match, index) => {
    const tagIndex = parseInt(index);
    return aTags[tagIndex];
  });

  // 중첩 a 태그 수정 - 더 일반적인 패턴으로 수정
  processedText = processedText.replace(/<a\s+href="<a\s+href="([^"]+)"[^>]*>([^<]*)<\/a>"/g, '<a href="$1"');

  // 닫는 태그 중첩 수정
  processedText = processedText.replace(/<\/a><\/a>/g, '</a>');

  // 링크 안의 링크를 수정 (마크다운 링크 변환 후 URL 패턴 매칭으로 인한 중첩)
  processedText = processedText.replace(/<a[^>]*>(.*?)<a[^>]*>(.*?)<\/a>(.*?)<\/a>/g, '<a href="$1">$2</a>');

  // console.log('출력 텍스트:', processedText);
  // console.log('===================== LINKIFY 함수 실행 완료 =====================');
  return processedText;
}; 